import { createContext, useState, useRef } from "react";
import { Action, ObjectType } from "../data/constants";
import { useUndoRedo } from "../hooks";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getDrawDBApp } from "ee/selectors/drawdbSelectors";
import { editDrawDB } from "ee/actions/drawdbActions";
import { debounce } from "lodash";

export const TypesContext = createContext(null);

export default function TypesContextProvider({ children }) {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const { setUndoStack, setRedoStack } = useUndoRedo();

  const dispatch = useDispatch();
  const drawDBApp = useSelector(getDrawDBApp);

  const debouncedDispatch = useRef(
    debounce((action) => {
      dispatch(action);
    }, 300),
  ).current;

  const addType = (data, addToHistory = true) => {
    if (data) {
      setTypes((prev) => {
        const temp = prev.slice();
        temp.splice(data.id, 0, data);
        const types = temp.map((t, i) => ({ ...t, id: i }));
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, types },
          }),
        );
        return types;
      });
    } else {
      setTypes((prev) => {
        const types = [
          ...prev,
          {
            name: `type_${prev.length}`,
            fields: [],
            comment: "",
          },
        ];
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, types },
          }),
        );
        return types;
      });
    }
    if (addToHistory) {
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.ADD,
          element: ObjectType.TYPE,
          message: t("add_type"),
        },
      ]);
      setRedoStack([]);
    }
  };

  const deleteType = (id, addToHistory = true) => {
    if (addToHistory) {
      message.success(t("type_deleted"));
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.DELETE,
          element: ObjectType.TYPE,
          id: id,
          data: types[id],
          message: t("delete_type", {
            typeName: types[id].name,
          }),
        },
      ]);
      setRedoStack([]);
    }
    setTypes((prev) => {
      const types = prev.filter((e, i) => i !== id);
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, types },
        }),
      );
      return types;
    });
  };

  const updateType = (id, values) => {
    setTypes((prev) => {
      const types = prev.map((e, i) => (i === id ? { ...e, ...values } : e));
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, types },
        }),
      );
      return types;
    });
  };

  return (
    <TypesContext.Provider
      value={{
        types,
        setTypes,
        addType,
        updateType,
        deleteType,
        typesCount: types.length,
      }}
    >
      {children}
    </TypesContext.Provider>
  );
}
